import { ReactNode, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { NacFormValues, isPrivato } from "../utils/nacValidation";

type Lookup = { id?: number; value: string };

const EMPTY = "—";

function Tile({ label, children, wide }: { label: string; children?: ReactNode; wide?: boolean }) {
  const empty = children === undefined || children === null || children === "";
  return (
    <div className={`rounded bg-gray-50 p-3 ${wide ? "md:col-span-2" : ""}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 whitespace-pre-wrap break-words font-semibold text-blue-700">{empty ? EMPTY : children}</div>
    </div>
  );
}

function Chips({ items }: { items: string[] }) {
  if (items.length === 0) return <>{EMPTY}</>;
  return (
    <span className="flex flex-wrap gap-2">
      {items.map((i) => (
        <span key={i} className="rounded border border-blue-200 bg-white px-2 py-0.5 text-sm font-medium text-blue-700">{i}</span>
      ))}
    </span>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border border-neutral-200 bg-white p-5">
      <h2 className="mb-4 text-xl font-bold">{title}</h2>
      {children}
    </section>
  );
}

const Grid = ({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) => (
  <div className={`grid grid-cols-1 gap-3 ${cols === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>{children}</div>
);

const names = (ids: Array<number | string>, rows: Lookup[] = []) =>
  ids.map((id) => rows.find((r) => (r.id ?? r.value) === id)?.value ?? String(id));

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");

type Candidate = { codice_cliente: string; ragione_sociale?: string | null; stato_cliente?: string | null };

export default function NacReadOnly({
  values: v,
  lookups,
  candidates = [],
  onPick,
  hideConfirmation = false,
  hideCommercial = false,
}: {
  values: NacFormValues;
  lookups: any;
  candidates?: Candidate[];
  onPick?: (codice: string) => void;
  /** Nasconde il blocco di conferma (pagina pubblica del cliente, dove la conferma e' la risposta stessa). */
  hideConfirmation?: boolean;
  /** Nasconde linguetta e blocco dei dati commerciali (valutazioni interne dell'agente): pagina pubblica del cliente. */
  hideCommercial?: boolean;
}) {
  const privato = isPrivato(v);
  const [tab, setTab] = useState<"richiesta" | "commerciale">("richiesta");
  const tabCls = (active: boolean) =>
    `inline-flex items-center rounded-lg px-6 py-3 text-base font-semibold ${active ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`;
  return (
    <>
      {candidates.length > 0 && (
        <Block title="Scegli il codice cliente">
          <p className="mb-3 text-sm text-gray-700">
            In AS400 esistono più clienti con {privato ? "questo codice fiscale" : "questa Partita IVA"}. Scegli quello corretto per chiudere la richiesta.
          </p>
          <div className="relative">
            <Swiper
              className="mySwiper pb-10 pt-4"
              slidesPerView={1}
              spaceBetween={20}
              navigation={{ nextEl: ".nac-cand-next", prevEl: ".nac-cand-prev" }}
              breakpoints={{ 640: { slidesPerView: 2 }, 768: { slidesPerView: 3 }, 1024: { slidesPerView: 4 } }}
              pagination={{ clickable: true, dynamicBullets: true }}
              modules={[Navigation, Pagination]}
            >
              {candidates.map((x: any, n: number) => (
                <SwiperSlide key={n} className="!h-auto">
                  <div className="flex h-full min-h-[200px] flex-col justify-center gap-1 rounded-2xl border border-blue-200 bg-white p-5 text-sm shadow-lg">
                    <p className="break-words text-base font-medium text-blue-600">{x.codice_cliente}</p>
                    <p className="break-words text-gray-900">{x.ragione_sociale || "—"}</p>
                    <p className="break-words text-gray-600">{x.stato_cliente || "—"}</p>
                    {onPick && (
                      <button type="button" className="mt-2 w-full rounded bg-blue-600 px-3 py-2 text-white hover:bg-blue-700" onClick={() => onPick(x.codice_cliente)}>
                        Scegli
                      </button>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {candidates.length > 1 && (
              <>
                <button type="button" aria-label="Precedente" className="nac-cand-prev absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 max-sm:top-auto max-sm:bottom-1 max-sm:h-8 max-sm:w-8 max-sm:translate-y-0 items-center justify-center rounded-full border border-blue-200 bg-white shadow-lg transition hover:scale-110 [&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40 left-2 sm:-left-12">
                  <span className="material-symbols-outlined text-blue-600" translate="no">chevron_left</span>
                </button>
                <button type="button" aria-label="Successivo" className="nac-cand-next absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 max-sm:top-auto max-sm:bottom-1 max-sm:h-8 max-sm:w-8 max-sm:translate-y-0 items-center justify-center rounded-full border border-blue-200 bg-white shadow-lg transition hover:scale-110 [&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40 right-2 sm:-right-12">
                  <span className="material-symbols-outlined text-blue-600" translate="no">chevron_right</span>
                </button>
              </>
            )}
          </div>
        </Block>
      )}

      {/* --prevent-on-load-init: Preline (PrivateRoute) inizializza ogni [role=tablist] e va in errore sulle nostre schede. */}
      {!hideCommercial && (
      <nav className="--prevent-on-load-init mb-6 flex gap-x-2" aria-label="Tabs" role="tablist">
        <button type="button" role="tab" id="nac-tab-richiesta" aria-selected={tab === "richiesta"} aria-controls="nac-panel-richiesta" className={tabCls(tab === "richiesta")} onClick={() => setTab("richiesta")}>
          Richiesta
        </button>
        <button type="button" role="tab" id="nac-tab-commerciale" aria-selected={tab === "commerciale"} aria-controls="nac-panel-commerciale" className={tabCls(tab === "commerciale")} onClick={() => setTab("commerciale")}>
          Dettaglio commerciale
        </button>
      </nav>
      )}

      <div id="nac-panel-richiesta" role="tabpanel" aria-labelledby="nac-tab-richiesta" hidden={tab !== "richiesta"}>
      <Block title="1. Identificazione fiscale">
        <Grid>
          <Tile label="Tipologia soggetto">{v.tipologia_soggetto}</Tile>
          <Tile label={privato ? "Nome e cognome" : "Ragione sociale"}>{v.ragione_sociale}</Tile>
          {!privato && <Tile label="Partita IVA">{v.partita_iva}</Tile>}
          <Tile label="Codice fiscale">{v.codice_fiscale}</Tile>
          <Tile label={privato ? "Residenza" : "Indirizzo sede legale"}>{[v.indirizzo, v.numero_civico].filter(Boolean).join(", ")}</Tile>
          <Tile label="CAP / Comune / Provincia">{[v.cap, v.comune, v.provincia && `(${v.provincia})`].filter(Boolean).join(" ")}</Tile>
          <Tile label="Nazione">{v.nazione}</Tile>
          {!privato && <Tile label="Data inizio attività">{formatDate(v.data_inizio_attivita)}</Tile>}
          <Tile label="PEC">{v.pec}</Tile>
          <Tile label="Codice SDI">{v.codice_sdi}</Tile>
        </Grid>
      </Block>

      <Block title={privato ? "2. Contatti" : "2. Contatti aziendali"}>
        <Grid>
          <Tile label="Telefono principale">{v.telefono}</Tile>
          <Tile label="Cellulare">{v.cellulare}</Tile>
          <Tile label="E-mail">{v.email}</Tile>
          {!privato && <Tile label="Sito web">{v.sito_web}</Tile>}
        </Grid>
        {!privato && (
          <>
            <h3 className="mb-2 mt-5 font-bold">Referenti</h3>
            <div className="relative">
              <Swiper
                className="mySwiper pb-10 pt-4"
                slidesPerView={1}
                spaceBetween={20}
                navigation={{ nextEl: ".nac-ref-next", prevEl: ".nac-ref-prev" }}
                breakpoints={{ 640: { slidesPerView: 2 }, 768: { slidesPerView: 3 }, 1024: { slidesPerView: 4 } }}
                pagination={{ clickable: true, dynamicBullets: true }}
                modules={[Navigation, Pagination]}
              >
                {(v.referenti ?? []).map((x: any, n: number) => (
                  <SwiperSlide key={n} className="!h-auto">
                    <div className="flex h-full min-h-[200px] flex-col justify-center gap-1 rounded-2xl border border-blue-200 bg-white p-5 text-sm shadow-lg">
                      <p className="break-words text-base font-medium text-blue-600">
                        {x.nome_cognome}
                        {x.principale && <span className="ml-1 rounded-full bg-blue-50 px-2 py-0.5 align-middle text-xs font-medium text-blue-600">principale</span>}
                      </p>
                      <p className="break-words text-gray-900">{x.ruolo || "—"}</p>
                      <p className="break-words text-gray-600">{x.telefono || "—"}</p>
                      <p className="break-words text-gray-600">{x.email || "—"}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              {(v.referenti ?? []).length > 1 && (
                <>
                  <button type="button" aria-label="Precedente" className="nac-ref-prev absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 max-sm:top-auto max-sm:bottom-1 max-sm:h-8 max-sm:w-8 max-sm:translate-y-0 items-center justify-center rounded-full border border-blue-200 bg-white shadow-lg transition hover:scale-110 [&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40 left-2 sm:-left-12">
                    <span className="material-symbols-outlined text-blue-600" translate="no">chevron_left</span>
                  </button>
                  <button type="button" aria-label="Successivo" className="nac-ref-next absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 max-sm:top-auto max-sm:bottom-1 max-sm:h-8 max-sm:w-8 max-sm:translate-y-0 items-center justify-center rounded-full border border-blue-200 bg-white shadow-lg transition hover:scale-110 [&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40 right-2 sm:-right-12">
                    <span className="material-symbols-outlined text-blue-600" translate="no">chevron_right</span>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </Block>

      <Block title="3. Indirizzi di spedizione">
        <div className="relative">
          <Swiper
            className="mySwiper pb-10 pt-4"
            slidesPerView={1}
            spaceBetween={20}
            navigation={{ nextEl: ".nac-addr-next", prevEl: ".nac-addr-prev" }}
            breakpoints={{ 640: { slidesPerView: 2 }, 768: { slidesPerView: 3 }, 1024: { slidesPerView: 4 } }}
            pagination={{ clickable: true, dynamicBullets: true }}
            modules={[Navigation, Pagination]}
          >
            {(v.indirizzi_spedizione ?? []).map((x: any, n: number) => (
              <SwiperSlide key={n} className="!h-auto">
                <div className="flex h-full min-h-[200px] flex-col justify-center gap-1 rounded-2xl border border-blue-200 bg-white p-5 text-sm shadow-lg">
                  <p className="break-words text-base font-medium text-blue-600">
                    {x.nome_destinazione}
                    {x.predefinito && <span className="ml-1 rounded-full bg-blue-50 px-2 py-0.5 align-middle text-xs font-medium text-blue-600">predefinita</span>}
                  </p>
                  <p className="break-words text-gray-900">{x.destinatario}</p>
                  <p className="break-words text-gray-600">{[x.indirizzo, x.numero_civico].filter(Boolean).join(", ")}</p>
                  <p className="break-words text-gray-600">{[x.cap, x.comune, x.provincia && `(${x.provincia})`].filter(Boolean).join(" ")}</p>
                  {(x.referente_consegna || x.telefono_consegna) && <p className="break-words text-gray-600">{[x.referente_consegna, x.telefono_consegna].filter(Boolean).join(" · ")}</p>}
                  {(x.orari_ricevimento || x.note_logistiche) && <p className="break-words text-gray-600">{[x.orari_ricevimento, x.note_logistiche].filter(Boolean).join(" – ")}</p>}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          {(v.indirizzi_spedizione ?? []).length > 1 && (
            <>
              <button type="button" aria-label="Precedente" className="nac-addr-prev absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 max-sm:top-auto max-sm:bottom-1 max-sm:h-8 max-sm:w-8 max-sm:translate-y-0 items-center justify-center rounded-full border border-blue-200 bg-white shadow-lg transition hover:scale-110 [&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40 left-2 sm:-left-12">
                <span className="material-symbols-outlined text-blue-600" translate="no">chevron_left</span>
              </button>
              <button type="button" aria-label="Successivo" className="nac-addr-next absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 max-sm:top-auto max-sm:bottom-1 max-sm:h-8 max-sm:w-8 max-sm:translate-y-0 items-center justify-center rounded-full border border-blue-200 bg-white shadow-lg transition hover:scale-110 [&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40 right-2 sm:-right-12">
                <span className="material-symbols-outlined text-blue-600" translate="no">chevron_right</span>
              </button>
            </>
          )}
        </div>
      </Block>

      <Block title="4. Dati amministrativi">
        <Grid>
          <Tile label="Condizioni di pagamento">{v.condizioni_pagamento}</Tile>
          <Tile label="Banca d'appoggio">{v.banca_appoggio}</Tile>
          <Tile label="IBAN">{v.iban}</Tile>
          <Tile label="E-mail invio documenti amministrativi">{v.email_documenti}</Tile>
          <Tile label="Note amministrative" wide>{v.note_amministrative}</Tile>
        </Grid>
      </Block>

      </div>

      {!hideCommercial && (
      <div id="nac-panel-commerciale" role="tabpanel" aria-labelledby="nac-tab-commerciale" hidden={tab !== "commerciale"}>
      <Block title="5. Dati commerciali">
        <Grid>
          <Tile label="Categoria vendita"><Chips items={names(v.categoria_vendita, lookups?.categoria_vendita)} /></Tile>
          <Tile label="Tipologia cliente"><Chips items={names(v.tipologia_cliente, lookups?.tipologia_cliente)} /></Tile>
          {!privato && (
            <>
              <Tile label="Numero cucine esposte">{v.numero_cucine_esposte}</Tile>
              <Tile label="Numero venditori">{v.numero_venditori}</Tile>
              <Tile label="Stima cucine vendute annualmente">{v.stima_cucine_anno}</Tile>
            </>
          )}
          <Tile label="Potenziale acquisto annuo CEDI">{v.potenziale_acquisto}</Tile>
          {!privato && (
            <>
              <Tile label="Marche cucine trattate"><Chips items={names(v.marca_cucina, lookups?.marca_cucina)} /></Tile>
              <Tile label="Marche elettrodomestici trattate"><Chips items={names(v.marca_elettrodomestici, lookups?.marca_elettrodomestici)} /></Tile>
              <Tile label="Distributori concorrenti"><Chips items={names(v.distributore, lookups?.distributore)} /></Tile>
            </>
          )}
          <Tile label="Valutazione commerciale (1–10)">{v.valutazione_commerciale}</Tile>
          <Tile label="Valutazione amministrativa (1–10)">{v.valutazione_amministrativa}</Tile>
          <Tile label="Commento" wide>{v.commento}</Tile>
        </Grid>
      </Block>

      </div>
      )}

      <div hidden={tab !== "richiesta"}>
      {!hideConfirmation && (
      <Block title="Conferma cliente">
        <Grid>
          <Tile label="Consenso invio telematico documenti">
            {v.consenso_invio_telematico === "si" ? "Sì" : v.consenso_invio_telematico === "no" ? "No" : ""}
          </Tile>
        </Grid>
      </Block>
      )}
      </div>
    </>
  );
}
